
/**
 * Product Gallery using Swiper
 */
class ProductGallery extends HTMLElement {
    constructor() {
        super();
        this.mainSwiper = null;
        this.thumbSwiper = null;
        this.resizeObserver = new ResizeObserver(() => this.setThumbHeight());
    }

    connectedCallback() {
        requestAnimationFrame(() => {
            setTimeout(() => this.init(), 0);
        });
    }

    init() {
        const mainEl = this.querySelector('.product-gallery-images');
        const thumbEl = this.querySelector('.product-gallery-thumbnails');
        if (!mainEl) return;

        const startIndex = parseInt(mainEl.dataset.start) || 0;

        if (thumbEl) {
            const thumbContainer = thumbEl.querySelector('.swiper');
            this.thumbNextBtn = thumbEl.querySelector('.splide__arrow--next');
            this.thumbPrevBtn = thumbEl.querySelector('.splide__arrow--prev');
            this.thumbSwiper = new Swiper(thumbContainer, {
                direction: 'horizontal',
                slidesPerView: 'auto',
                spaceBetween: 10,
                initialSlide: startIndex, 
                watchSlidesProgress: true,
                breakpoints: {
                    768: {direction: 'vertical'}
                },
                on: {
                    init: () => this.setThumbHeight()
                }
            });

            this.resizeObserver.observe(mainEl);
        }

        const effect = mainEl.dataset.effect || 'slide';
        const nextNavEl = mainEl.querySelector('.splide__arrow--next');
        const prevNavEl = mainEl.querySelector('.splide__arrow--prev');
        const mainOptions = {
            slidesPerView: 1,
            initialSlide: startIndex,
            autoHeight:true,
            effect: effect,
            speed: 500,
            thumbs: {
                swiper: this.thumbSwiper,
            },
            on: {
                slideChange: (swiper) => {
                    this.handleSlideChange(swiper);
                    this.updateNavigationState(swiper);
                },
                transitionEnd: () => this.pauseInactiveMedia(),
                imagesReady: () => this.setThumbHeight()
            }
        };

        if (effect === 'fade') {
            mainOptions.fadeEffect = { crossFade: true };
        }
        if (nextNavEl && prevNavEl) {
            mainOptions.navigation = {
                nextEl: nextNavEl,
                prevEl: prevNavEl
            };
        }

        this.mainSwiper = new Swiper(mainEl, mainOptions);
        this.updateNavigationState(this.mainSwiper);

        if (startIndex > 0 && this.mainSwiper.activeIndex !== startIndex) {
            this.mainSwiper.slideTo(startIndex, 0, false);
            this.thumbSwiper.update();
        }
        this.thumbNextBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.mainSwiper?.slideNext();
            this.updateNavigationState();
        });
        this.thumbPrevBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.mainSwiper?.slidePrev();
            this.updateNavigationState();
        });

        if (mainEl.classList.contains('has-lightbox')) {
            this.initLightbox();
        }
    }

    updateNavigationState(swiper) {
        const instance = swiper || this.mainSwiper;
        if (!instance) return;

        const { isBeginning, isEnd } = instance;

        if(this.thumbPrevBtn){
            this.thumbPrevBtn.disabled = isBeginning;
            this.thumbPrevBtn.classList.toggle('swiper-button-disabled', isBeginning);
        }
        if(this.thumbNextBtn){
            this.thumbNextBtn.disabled = isEnd;
            this.thumbNextBtn.classList.toggle('swiper-button-disabled', isEnd);
        }

    }

    setThumbHeight() {
        const mainEl = this.querySelector('.product-gallery-images');
        const thumbEl = this.querySelector('.product-gallery-thumbnails');
        if (!mainEl || !thumbEl) return;

        const container = thumbEl;
        if (!container) return;

        if (window.innerWidth >= 769) {
            const mainHeight = mainEl.offsetHeight;
            if (mainHeight > 0 && container.style.height !== `${mainHeight}px`) {
                container.style.height = `${mainHeight}px`;
                this.thumbSwiper?.update();
            }
        } else {
            if (container.style.height !== '') {
                container.style.height = '';
                this.thumbSwiper?.update();
            }
        }
    }

    handleSlideChange(swiper) {
        const badgeWrapper = this.querySelector('.variant-badge-wrapper');
        if (!badgeWrapper) return;

        const instance = swiper || this.mainSwiper;
        if (!instance) return; 

        const activeIndex = instance.activeIndex;
        if (this.thumbSwiper) {
            this.thumbSwiper.slideTo(activeIndex);
        }
        
        const thumbnails = this.querySelectorAll('.product-gallery-thumbnails-thumbnail');
        const currentThumb = thumbnails[activeIndex];
        
        if (currentThumb && currentThumb.dataset.variantIds && currentThumb.dataset.variantIds.trim() !== "") {
            badgeWrapper.style.display = 'flex';
        } else {
            badgeWrapper.style.display = 'none';
        }
    }

    pauseInactiveMedia(swiper) {
        const instance = swiper || this.mainSwiper;
        if (!instance || !instance.slides) return;
        
        instance.slides.forEach((slide, index) => {
            if (index !== instance.activeIndex) {
                const video = slide.querySelector('video');
                if (video) video.pause();
                const iframe = slide.querySelector('iframe');
                if (iframe) {
                    iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                }
            }
        });
    }

    initLightbox() {
        if (typeof PhotoSwipeLightbox !== 'undefined') {
            const lightbox = new PhotoSwipeLightbox({
                gallery: '.product-gallery-images.has-lightbox',
                children: '.swiper-slide a',
                pswpModule: () => Promise.resolve(PhotoSwipe)
            });
            lightbox.init();
        }
    }

    disconnectedCallback() {
        this.resizeObserver.disconnect();
        if (this.mainSwiper) this.mainSwiper.destroy();
        if (this.thumbSwiper) this.thumbSwiper.destroy();
    }
}
if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
}

// Product Rich media
class ProductRichMedia extends HTMLElement{
    constructor(){
        super();

        this.mediaPreview = this.querySelector('.product-rich-media-preview');
        this.mediaContent = this.querySelector('.product-rich-media-content');
        this.video = this.querySelector('video');
        this.modelViewer = this.querySelector('model-viewer');
        this.xrButton = this.querySelector('.product-xr-button');

        this.mediaPreview.addEventListener('click', () => {
            this.onClick();
        })

        if(this.xrButton){
            this.xrButton.addEventListener('click', () => {
                this.xrButtonClick();
            })
        }
    }

    onClick = () => {
        this.classList.add('active');

        if( this.video ){
            this.video.play();
        }

        if( this.modelViewer ){
            this.loadModelInteraction();
        }
    }

    xrButtonClick = () => {
        Shopify.loadFeatures(
            [
                {
                    name: 'shopify-xr',
                    version: '1.0',
                    onLoad: (errors) => this.setupShopifyXR(errors),
                }
            ]
        )
    }

    loadModelInteraction = () => {
        Shopify.loadFeatures(
            [
                {
                    name: 'model-viewer-ui',
                    version: '1.0',
                    onLoad: (errors) => this.initiateModelViewerUI(errors)
                }
            ]
        )
    }

    initiateModelViewerUI = (errors) => {
        if (errors){
            return false
        }
        this.modelViewerUI = new Shopify.ModelViewerUI(this.querySelector('model-viewer'))
    }

    setupShopifyXR = (errors) => {
        if (errors) {
            return false
        }
        if (!window.ShopifyXR) {
            document.addEventListener('shopify_xr_initialized', () =>
            this.setupShopifyXR()
            );
            return;
        }

        const modelJSON = this.xrButton.dataset.shopifyModel3dContent
    
        window.ShopifyXR.addModels(JSON.parse(modelJSON));
        window.ShopifyXR.setupXRElements();
      }
}
if (!customElements.get('product-rich-media')) {
customElements.define('product-rich-media', ProductRichMedia)
}

class ProductCollapsibleRow extends HTMLElement {
    constructor() {
        super();
        this.onHeaderClick = this.onClick.bind(this);
    }

    connectedCallback() {
        if (this.initialized) return;
        
        this.titleElement = this.querySelector('.product-collapsible-row__title');
        this.contentElement = this.querySelector('.product-collapsible-row__content');

        if (!this.titleElement || !this.contentElement) return;

        this.init();
        this.initialized = true;
    }

    init() {
        if (this.classList.contains('active_by_default')) {
            this.expand();
            this.classList.remove('active_by_default');
        } else {
            this.collapse();
        }

        this.titleElement.addEventListener('click', this.onHeaderClick);
        this.titleElement.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.onClick(event);
            }
        });
    }

    expand() {
        this.classList.add('active');
        this.titleElement.setAttribute('aria-expanded', 'true');
        
        this.contentElement.style.height = 'auto';
        const height = this.contentElement.scrollHeight; 
        this.contentElement.style.height = '0';
        
        void this.contentElement.offsetHeight;
        this.contentElement.style.height = height + 'px';

        this.contentElement.addEventListener('transitionend', () => {
            if (this.classList.contains('active')) {
                this.contentElement.style.height = 'auto';
            }
        }, { once: true });
    }

    collapse() {
        const height = this.contentElement.scrollHeight;
        this.contentElement.style.height = height + 'px';
        void this.contentElement.offsetHeight;

        this.classList.remove('active');
        this.titleElement.setAttribute('aria-expanded', 'false');
        this.contentElement.style.height = '0';
    }

    onClick(event) {
        event.preventDefault();
        if (this.classList.contains('active')) {
            this.collapse();
        } else {
            this.expand();
        }
    }
}

if (!customElements.get('product-collapsible-row')) {
    customElements.define('product-collapsible-row', ProductCollapsibleRow);
}

// Information popup
class ProductInfoPopup extends HTMLElement{
    constructor(){
        super();

        this.productInfoButtons = this.querySelectorAll('.product-info-popup__button');

        for(let productInfoButton of this.productInfoButtons){
            productInfoButton.addEventListener('click', (event) => {
                if( !event.target.closest('popup-modal')){
                    this.onClick(event, productInfoButton);
                }
            })
        }
    }

    onClose = () => {
        var popupModal = document.querySelector('.popup-modal');

        if(popupModal.classList.contains('product-info-popup-modal')) {
            popupModal.classList.remove('product-info-popup-modal');
        }
        popupModal.querySelector('.popup-modal__content').innerHTML = '';
    }
    

    onClick = (event, infoButton) => {
        event.preventDefault();

        var pageHandle = event.target.dataset.pageHandle; 
        this.classList.add('loading');
        var popupModal = document.querySelector('.popup-modal');

        if( !popupModal ){
            var quickviewModalString = `<popup-modal class="popup-modal product-info-popup-modal">
            <div class="popup-modal__overlay"></div>
            <div class="popup-modal__inner">
                <div class="popup-modal__content">
                </div>
                <button class="popup-modal__close">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="1.11092" y1="0.610962" x2="8.88909" y2="8.38914" stroke="currentColor" stroke-linecap="round"/>
                        <line x1="1.11096" y1="8.38915" x2="8.88914" y2="0.610971" stroke="currentColor" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>
            </popup-modal>`
            var quickviewModal = new DOMParser().parseFromString(quickviewModalString, 'text/html').querySelector('popup-modal')
            document.body.append(quickviewModal);
        }
        else{
            if(!popupModal.classList.contains('product-info-popup-modal')) {
                popupModal.classList.add('product-info-popup-modal');
            }
            popupModal.classList.remove('inactive');
        }

        if( !pageHandle ){
            var emptyText = infoButton.dataset.emptyText;
            if( emptyText ){
                var infoButtonWrap = document.createElement('div');
                infoButtonWrap.classList.add('page-description');
                infoButtonWrap.innerHTML = emptyText;
                var popupModalContent = document.querySelector('.popup-modal__content');
                popupModalContent.append(infoButtonWrap);
            }

            return false
        }

        fetch(`${window.Shopify.routes.root}pages/${pageHandle}`)
            .then((response) => {
                if (!response.ok) {
                    var error = new Error(response.status);
                    throw error;
                }

                return response.text();
            })
            .then((res)=>{ 
                var html = new DOMParser().parseFromString(res, 'text/html');
                var content = html.querySelector('.section-main-page-content');
                var popupModalContent = document.querySelector('.popup-modal__content');
                popupModalContent.append(content);
                this.popupCloseButton = document.querySelector('.popup-modal__close');
                this.popupOverlay = document.querySelector('.popup-modal__overlay');
                this.popupCloseButton.addEventListener('click', () => {
                    this.onClose();
                })

                this.popupOverlay.addEventListener('click', () => {
                    this.onClose();
                })
            })
    }
}
customElements.define('product-info-popup', ProductInfoPopup);

// Share product
class ProductShareIcons extends HTMLElement{
    constructor(){
        super();
        this.copyShareLink();
        this.shareIcons();
    }

    copyShareLink = () => {
        var shareCopyLink = this.querySelector('.product-share-copy-link > a');

        shareCopyLink.addEventListener('click', (event) => {
            event.preventDefault();
            navigator.clipboard.writeText(shareCopyLink.getAttribute('href')).then(()=>{ 
                shareCopyLink.parentElement.classList.add('copied')
                setTimeout(function(){
                    shareCopyLink.parentElement.classList.remove('copied')
                }, 3000)
            });
        })
    }

    shareIcons = () => {
        var shareIcons = this.querySelectorAll('.product-share-icon > a');

        for(let icon of shareIcons){
            icon.addEventListener('click', (event) => {
                event.preventDefault();
                var url = icon.getAttribute('href');
                window.open(url, 'popUpWindow', 'height=700, width=800, left=10, top=10, resizable=yes, scrollbars=yes, toolbar=yes, menubar=no, location=no, directories=no, status=yes');
            })
        }
    }

}
customElements.define('product-share-icons', ProductShareIcons)

// Product recommendation
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  async init() {
    const { productId, intent, limit, carousel, options} = this.dataset;
    const parentElement = this.closest(".section-product-recommendations");
    const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&section_id=product-recommendations&limit=${limit}&intent=${intent}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const section = doc.querySelector('.shopify-section');

      if (!section) return;
      
      let products = Array.from(section.querySelectorAll('.product'));
      const excludedIds = window.relateProductRemove || [];
      
      products = products.filter(p => !excludedIds.includes(p.dataset.productId));
      if (products.length === 0) return;
      
      if(parentElement) parentElement.style.display = "block";
      if (products.length && carousel === 'true') { 
        this.renderCarousel(products, section.querySelector('ul'), JSON.parse(options || '{}'));
      }

      this.initExternalApps(); 
    } catch (error) {
      console.error("Product Recommendations Error:", error);
    }
  }

  renderCarousel(products, listElement, swiperOptions) {
    const container = document.createElement('div');
    container.className = 'swiper';

    listElement.className = `swiper-wrapper ${this.dataset.is_small ? "has-product-size-small" : ""}`;
    products.forEach(product => {
      product.classList.add('swiper-slide');
      listElement.appendChild(product);
    });

    container.appendChild(listElement);

    if (this.dataset.arrows === 'true') {
      container.insertAdjacentHTML('beforeend', `
        <div class="swiper-button-prev"></div>
        <div class="swiper-button-next"></div>
      `);
    }
    if (this.dataset.progressBar === 'true') {
      container.insertAdjacentHTML('beforeend', '<div class="swiper-pagination"></div>');
    }

    this.innerHTML = '';
    this.appendChild(container);

    if (typeof Swiper !== 'undefined') {
      new Swiper(container, {...swiperOptions,a11y:{slideLabelMessage: 'Product {{index}} / {{slidesLength}}',slideRole: ''}});
    } else {
      console.warn('Swiper is not defined. Please load Swiper library.');
    }
  }

  initExternalApps() {
    this.querySelectorAll('.product').forEach(product => {
      const badgeText = product.querySelector('.jdgm-prev-badge__text');
      const badgeStars = product.querySelector('.jdgm-prev-badge__stars');
      if (badgeText && badgeStars?.dataset?.score) {
        badgeText.innerText = badgeStars.dataset.score;
      }
    });
  }
}
customElements.define('product-recommendations', ProductRecommendations)

class ProductVariantsSelectors extends HTMLElement {
    constructor() {
        super();
        this.abortController = null;
        this.initialized = false;
    }

    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        const currentVariant = new URLSearchParams(window.location.search).get('variant');
        this.productVariantSelectors = this.querySelectorAll('select');

        let defaultVariantSelect = null;

        this.productVariantSelectors.forEach((variantSelect) => {
            variantSelect.addEventListener('change', () => this.onVariantChange(variantSelect));

            const selectedVariantId = variantSelect.options[variantSelect.selectedIndex]?.value;
            if (!defaultVariantSelect && selectedVariantId && selectedVariantId !== 'none') {
                defaultVariantSelect = variantSelect;
            }

            if (currentVariant && selectedVariantId === currentVariant) {
                defaultVariantSelect = variantSelect;
            }
        });

        if (defaultVariantSelect) {
            this.onVariantChange(defaultVariantSelect);
        }
    }

    onVariantChange = (productVariantSelector) => {
        const selectedOption = productVariantSelector.options[productVariantSelector.selectedIndex];
        if (!selectedOption) return;

        const productContainer = productVariantSelector.closest('.product') || this;
        const variantId = selectedOption.value;
        const priceContainers = Array.from(productContainer.querySelectorAll('.product-price'))
            .filter((priceElement) => !priceElement.closest('sticky-bar'));
        const addToCartButton = productContainer.querySelector('.product-add-to-cart');
        const inventoryElement = productContainer.querySelector('.product-inventory-qty');
        const quantityInput = productContainer.querySelector('.product-quantity-picker-input');
        const badgeWrapper = productContainer.querySelector('.variant-badge-wrapper');
        const productDiscount = productContainer.querySelector('[data-price][data-discount]')?.dataset.discount || null;

        if (this.abortController) this.abortController.abort();

        if (variantId === 'none') {
            if (addToCartButton) {
                addToCartButton.disabled = true;
                addToCartButton.innerHTML = `<span>${addToCartButton.dataset.textUnavailable || ''}</span>`;
            }
            if (inventoryElement) inventoryElement.innerHTML = '';
            priceContainers.forEach((priceElement) => priceElement.classList.add('hide'));
            return;
        }

        priceContainers.forEach((priceElement) => priceElement.classList.remove('hide'));

        this.updateStockAndCartUI({
            selectedOption,
            productContainer,
            priceContainers,
            addToCartButton,
            inventoryElement,
            quantityInput,
            badgeWrapper
        });
        this.updateGallery(productContainer, variantId);

        this.abortController = new AbortController();
        const variantUrl = `${window.Shopify?.routes?.root || '/'}variants/${variantId}.json`;

        fetch(variantUrl, { signal: this.abortController.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
                return response.json();
            })
            .then((response) => {
                if (!response.product_variant) return;

                priceContainers.forEach((priceElement) => {
                    this.updatePriceUI(
                        selectedOption,
                        productContainer,
                        response.product_variant,
                        priceElement,
                        document.body.dataset.moneyFormat || '${{amount}}',
                        productDiscount
                    );
                });
                document.dispatchEvent(new CustomEvent('shopifyProductVariantFetched', {
                    detail: { variant: response.product_variant, variantId, source: this }
                }));
            })
            .catch((error) => {
                if (error.name !== 'AbortError') console.error('Error fetching variant data:', error);
            });
    };

    updateStockAndCartUI({ selectedOption, productContainer, priceContainers, addToCartButton, inventoryElement, quantityInput, badgeWrapper }) {
        const data = selectedOption.dataset;
        const stockQuantity = Number.parseInt(data.inventoryQuantity, 10) || 0;
        const inventoryManagement = data.inventoryManagement || '';
        const inventoryPolicy = data.inventoryPolicy || '';
        const available = data.available === 'true';
        priceContainers.forEach((priceContainer) => {
            const priceElement = priceContainer.querySelector('[data-price]');
            const comparePriceElement = priceContainer.querySelector('[data-price-compare]');
            if (priceElement && data.priceUninitialized) priceElement.dataset.value = data.priceUninitialized;
            if (comparePriceElement && data.comparepriceUninitialized) comparePriceElement.dataset.value = data.comparepriceUninitialized;
        });
        if (badgeWrapper) badgeWrapper.innerHTML = data.badge_arry || '';

        if (quantityInput) {
            if (inventoryManagement !== '' && inventoryPolicy === 'deny') {
                quantityInput.setAttribute('max', Math.max(0, stockQuantity));
            } else {
                quantityInput.removeAttribute('max');
            }
        }

        if (inventoryElement) {
            const lowStockThreshold = Number.parseInt(inventoryElement.dataset.lowStockThreshold, 10) || 0;
            let stockHtml;

            if (stockQuantity !== 0) {
                if (lowStockThreshold >= stockQuantity) {
                    stockHtml = `<p class="low">${(inventoryElement.dataset.textLowStock || '').replace('%s', stockQuantity)}</p>`;
                } else if (inventoryManagement !== '' && inventoryPolicy !== 'continue') {
                    stockHtml = `<p class="normal">${(inventoryElement.dataset.textInStock || '').replace('%s', stockQuantity)}</p>`;
                } else {
                    stockHtml = `<p class="normal">${inventoryElement.dataset.textInStockNoTrack || ''}</p>`;
                }
            } else if (inventoryManagement === '') {
                stockHtml = `<p class="normal">${inventoryElement.dataset.textInStockNoTrack || ''}</p>`;
            } else {
                stockHtml = `<p class="sold">${(inventoryElement.dataset.textSold || '').replace('%s', stockQuantity)}</p>`;
            }
            inventoryElement.innerHTML = stockHtml;
        }

        if (addToCartButton) {
            addToCartButton.dataset.preOrderPopupHtml = data.preOrderPopupHtml || '';
            const buttonText = available
                ? (data.preOrderPopupHtml ? addToCartButton.dataset.textPreOrder : addToCartButton.dataset.textAddToCart)
                : addToCartButton.dataset.textSoldOut;
            addToCartButton.innerHTML = `<span data-text="${buttonText || ''}"><span>${buttonText || ''}</span></span>`;
            addToCartButton.disabled = !available;
        }
    }

    updatePriceUI(selectedOption, productContainer, productVariant, productPriceElement, moneyFormat, productDiscountOverride = null) {
        if (!productPriceElement) return;

        const priceElement = productPriceElement.querySelector('[data-price]');
        if (!priceElement) return;

        let comparePriceElement = productPriceElement.querySelector('[data-price-compare]');
        const unitPriceWrapper = productPriceElement.querySelector('.product-price-unit');
        let saleBadgeElement = productPriceElement.parentElement?.querySelector('[data-price-badge]');
        const priceValue = Number.parseFloat(productVariant.price);
        const comparePriceValue = productVariant.compare_at_price ? Number.parseFloat(productVariant.compare_at_price) : null;
        if (!Number.isFinite(priceValue)) return;

        const hasComparePrice = Number.isFinite(comparePriceValue) && comparePriceValue > priceValue;
        const variantDiscount = selectedOption.dataset.discount || null;
        const productDiscount = productDiscountOverride || priceElement.dataset.discount || null;
        const discount = variantDiscount || productDiscount;
        let displayComparePrice = null;
        let displayFinalPrice = priceValue;

        if (discount) {
            const basePrice = hasComparePrice ? comparePriceValue : priceValue;
            displayComparePrice = basePrice;
            if (discount.includes('-')) {
                displayFinalPrice = basePrice - Number.parseFloat(discount.split('-').pop()) / 100;
            } else {
                displayFinalPrice = basePrice * (1 - Number.parseFloat(discount));
            }
        } else if (hasComparePrice) {
            displayComparePrice = comparePriceValue;
        }

        priceElement.innerHTML = Shopify.formatMoney(displayFinalPrice.toFixed(2), moneyFormat);
        priceElement.dataset.value = Math.round(displayFinalPrice * 100);

        if (displayComparePrice !== null) {
            const formattedComparePrice = Shopify.formatMoney(displayComparePrice.toFixed(2), moneyFormat);
            if (!comparePriceElement) {
                comparePriceElement = document.createElement('span');
                comparePriceElement.setAttribute('data-price-compare', '');
                priceElement.parentNode.insertBefore(comparePriceElement, priceElement);
            }
            if (!comparePriceElement.closest('sticky-bar')) {
                comparePriceElement.innerHTML = formattedComparePrice;
                comparePriceElement.dataset.value = Math.round(displayComparePrice * 100);
            }
        } else if (comparePriceElement && !comparePriceElement.closest('sticky-bar')) {
            comparePriceElement.innerHTML = '';
            comparePriceElement.removeAttribute('data-value');
        }

        const basePrice = hasComparePrice ? comparePriceValue : priceValue;

        if (variantDiscount) {
            const discountNumber = this.getDiscountBadgeNumber(variantDiscount, basePrice);
            if (saleBadgeElement && discountNumber !== null) {
                this.updateBadgeNumber(saleBadgeElement, discountNumber);
            }
            this.updateCouponBarDiscount(productContainer, discountNumber);
        } else if (productDiscount) {
            if (saleBadgeElement) this.restoreBadgeText(saleBadgeElement);
            const discountNumber = this.getDiscountBadgeNumber(productDiscount, basePrice);
            this.updateCouponBarDiscount(productContainer, discountNumber);
        } else {
            if (displayComparePrice !== null && displayComparePrice > displayFinalPrice) {
                const percentage = Math.round((displayComparePrice - displayFinalPrice) * 100 / displayComparePrice);
                const badgeHtml = `${productPriceElement.dataset.textSave || ''} ${percentage}%`;
                if (!saleBadgeElement) {
                    saleBadgeElement = document.createElement('div');
                    saleBadgeElement.className = 'e_product_badge';
                    saleBadgeElement.dataset.priceBadge = '';
                    saleBadgeElement.dataset.messageGroup = 'none';
                    saleBadgeElement.style.cssText = 'color: #261622; background:#D7F8D6;';
                    productPriceElement.parentElement?.append(saleBadgeElement);
                }
                saleBadgeElement.innerHTML = badgeHtml;
                this.updateCouponBarDiscount(productContainer, percentage);
            } else {
                saleBadgeElement?.remove();
                this.updateCouponBarDiscount(productContainer, null);
            }
        }

        const unitPriceValue = productVariant.unit_price;
        if (unitPriceValue && productVariant.unit_price_measurement) {
            const referenceValue = productVariant.unit_price_measurement.reference_value;
            const referenceUnit = productVariant.unit_price_measurement.reference_unit;
            const unitPrice = Shopify.formatMoney(Number.parseFloat(unitPriceValue).toFixed(2), moneyFormat);
            const unitBase = referenceValue > 1 ? referenceValue + referenceUnit : referenceUnit;
            const unitPriceElement = productPriceElement.querySelector('[data-price-unit-price]');
            const unitBaseElement = productPriceElement.querySelector('[data-price-unit-base]');

            if (unitPriceWrapper && unitPriceElement && unitBaseElement) {
                unitPriceElement.innerHTML = unitPrice;
                unitBaseElement.innerHTML = unitBase;
            } else if (!unitPriceWrapper) {
                const newUnitPriceWrapper = document.createElement('span');
                newUnitPriceWrapper.className = 'product-price-unit';
                newUnitPriceWrapper.innerHTML = `<span data-price-unit-price>${unitPrice}</span>/<span data-price-unit-base>${unitBase}</span>`;
                productPriceElement.append(newUnitPriceWrapper);
            }
        } else {
            unitPriceWrapper?.remove();
        }
    }

    getDiscountBadgeNumber(discount, basePrice) {
        const discountValue = Number.parseFloat(discount);
        if (!Number.isFinite(discountValue) || !Number.isFinite(basePrice) || basePrice <= 0) return null;

        if (discount.includes('-')) {
            const discountCents = Math.abs(discountValue);
            return Math.ceil((discountCents / 100) * 100 / basePrice);
        }

        return Math.floor(discountValue * 100);
    }

    updateBadgeNumber(badgeElement, value) {
        const textNodes = [];
        const walker = document.createTreeWalker(badgeElement, NodeFilter.SHOW_TEXT);
        let textNode;

        while ((textNode = walker.nextNode())) {
            textNodes.push(textNode);
        }

        if (!textNodes.length) return;

        if (!badgeElement.dataset.originalTextNodes) {
            badgeElement.dataset.originalTextNodes = JSON.stringify(textNodes.map((node) => node.nodeValue));
        }

        const numberPattern = /\d+(?:[.,]\d+)?%?/;
        for (const node of textNodes) {
            if (numberPattern.test(node.nodeValue)) {
                node.nodeValue = node.nodeValue.replace(numberPattern, `${value}%`);
                return;
            }
        }
    }

    restoreBadgeText(badgeElement) {
        const originalTextNodes = badgeElement.dataset.originalTextNodes;
        if (!originalTextNodes) return;

        const textNodes = [];
        const walker = document.createTreeWalker(badgeElement, NodeFilter.SHOW_TEXT);
        let textNode;

        while ((textNode = walker.nextNode())) {
            textNodes.push(textNode);
        }

        JSON.parse(originalTextNodes).forEach((text, index) => {
            if (textNodes[index]) textNodes[index].nodeValue = text;
        });
    }

    updateCouponBarDiscount(productContainer, value) {
        const couponBarDiscount = productContainer.querySelector('.code-coupon_bar .left>span:first-child');
        if (!couponBarDiscount) return;

        if (value === null || value === undefined || Number.isNaN(value)) {
            couponBarDiscount.textContent = '';
            return;
        }

        couponBarDiscount.textContent = String(value);
    }

    updateGallery(productContainer, variantId) {
        const gallery = productContainer.querySelector('product-gallery');
        if (!gallery?.mainSwiper) return;

        for (const thumbnail of productContainer.querySelectorAll('.product-image-has-variant-ids')) {
            const variantIds = (thumbnail.dataset.variantIds || '').split(/[\s,]+/).filter(Boolean);
            if (!variantIds.includes(String(variantId))) continue;

            const targetIndex = Number.parseInt(thumbnail.dataset.thumbnailIndex, 10) - 1;
            if (!Number.isNaN(targetIndex)) {
                requestAnimationFrame(() => gallery.mainSwiper?.slideTo(targetIndex));
            }
        }
    }

    disconnectedCallback() {
        this.abortController?.abort();
    }
}
customElements.define('product-variants-selector', ProductVariantsSelectors)

class PickupAvailability extends HTMLElement {
    constructor(){
        super();

        this.productVariantSelectors = document.querySelectorAll('.product-variants-selector > select');
        this.PickupAvailabilities = document.querySelectorAll('pickup-availability');

        for(let pickupAvailability of this.PickupAvailabilities){
            document.addEventListener('click', (event) => {
                var isButton = event.target.classList.contains('pickup-availability-information__button');
                var isClose = event.target.classList.contains('pickup-availability-panel__close');

                if( !isClose && event.target.parentElement ){
                    isClose = event.target.parentElement.classList.contains('pickup-availability-panel__close')
                }
                
                if(isButton){
                    this.onClick(event, pickupAvailability);
                }

                if(isClose){
                    this.closeClick(pickupAvailability);
                }

                if (event.target !== pickupAvailability && !pickupAvailability.contains(event.target)) {
                    this.closeClick(pickupAvailability)
                }
            })
        }

        this.productVariantSelectors.forEach(productVariantSelector => {
            productVariantSelector.addEventListener('change', () => {
                this.onVariantChange(productVariantSelector)
            })
        })
    }

    onClick(event, pickupAvailability){
        var panel = pickupAvailability.querySelector('.pickup-availability-panel');

        if( panel ){
            panel.classList.add('active');
            var panelHeight = panel.offsetHeight;
            var x = event.clientX, 
            y = event.clientY;

            if( y > panelHeight ){
                panel.classList.add('place-above');
            }
            else if( (window.innerHeight - y) > panelHeight ){
                panel.classList.add('place-below');
            }
            else{
                panel.classList.add('place-center');
            }
        }
    }

    closeClick(pickupAvailability){
        var panel = pickupAvailability.querySelector('.pickup-availability-panel');

        if( panel ){
            panel.classList.remove('active');
            panel.classList.remove('place-above', 'place-below', 'place-center');
        }
    }

    onVariantChange(productVariantSelector){
        var selectedIndex = productVariantSelector.selectedIndex;
        var variantId = productVariantSelector[selectedIndex].getAttribute('value');

        var pickupAvailability = productVariantSelector.closest('.product').querySelector('pickup-availability')

        fetch(`/variants/${variantId}/?section_id=pickup-availability`)
            .then(response => response.text())
            .then(text => {
                var html = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
                pickupAvailability.innerHTML = html.innerHTML;
            })
            .catch(e => {
                console.error(e);
            });
    }
}
customElements.define( 'pickup-availability', PickupAvailability );

// Recipient Form
class RecipientForm extends HTMLElement{
    constructor(){
        super();

        this.recipientToggle = this.querySelector('.recipient-form-toggle');
        this.recipientToggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.onClick();
        })
    }

    onClick = () => {
        var recipientForm = this.closest('.recipient-form');
        var recipientFormToggleInput = this.recipientToggle.querySelector('input');
        var recipientFormFields = recipientForm.querySelector('.recipient-form-fields');
        var recipientFormFieldsHeight = recipientFormFields.offsetHeight;

        if( recipientFormToggleInput.getAttribute('checked') ){
            recipientFormToggleInput.removeAttribute('checked')
        }
        else{
            recipientFormToggleInput.setAttribute('checked', true)
        }

        if( recipientForm.classList.contains('active') ){
            recipientFormFields.style.removeProperty('height');
            setTimeout(function(){
                recipientForm.classList.remove('active');
            }, 200)
        }
        else{
            recipientForm.classList.add('active');
            setTimeout(function(){
                recipientFormFields.style.height = recipientFormFieldsHeight + 'px';
            }, 10)
        }
    }

    onSubmit = () => {
        console.log("I run this before submit");

        this.productForm.submit();
    }
}
customElements.define('recipient-form', RecipientForm)

// Sticky add to cart 
class StickyBar extends HTMLElement{
    constructor(){
        super();

        this.select = document.querySelector('.product-variants-selector-select');
        this.stickyAddtocart = document.querySelector('.product-sticky-bar-add-to-cart');
        this.productOptionBox = document.querySelector('product-options.product-summary__variant-picker');
        this.textOff = this.dataset.textOff;
        this.match = this.dataset.match == 'true' ? true : false;
        this.optionGroups = this.querySelectorAll('.psb-option-group');
        this.btn = this.querySelector('.sticky_product_btn');
        this.variantFetchedHandler = (event) => this.updateStickyPrice(event);
        document.addEventListener('shopifyProductVariantFetched', this.variantFetchedHandler);
        this.stickyAddtocart.addEventListener('click', function(){
            this.closest(".product-summary").querySelector("#AddToCart").click();
        })
        this.select.addEventListener('change', () => {
            this.onVariantChange();
        })

        window.addEventListener('scroll', () => {
            const productAddtocart = this.closest('.product').querySelector('.product-add-to-cart');
            const footer = document.querySelector('.section-footer');
            const windowInnerHeight = window.innerHeight;
            const addtocartOffsetTop = productAddtocart.getBoundingClientRect().top;
            const footerOffsetTop = footer.getBoundingClientRect().top;

            if( addtocartOffsetTop < 0 && footerOffsetTop > windowInnerHeight ){
                this.classList.add('active');
            }
            else{
                this.classList.remove('active');
            }
        })

        this.bindOptionGroupEvents();
    }

    updateStickyPrice(event) {
        const source = event.detail?.source;
        const product = this.select?.closest('.product');
        if (!product || (source && source.closest('.product') !== product)) return;

        const productPriceElement = product.querySelector('.product-price');
        const stickyProductPrices = product.querySelectorAll('.product-sticky-bar-product-price');
        if (!productPriceElement) return;

        if (productPriceElement.innerHTML) {
            const selectedOption = this.select?.options[this.select.selectedIndex];
            const selectedDiscount = selectedOption?.dataset?.discount || selectedOption?.getAttribute('data-discount') || null;
            const stickyDiscount = this.getStickyDiscountPercentage(productPriceElement, selectedDiscount);

            if (this.match) {
                const price = productPriceElement.querySelector('[data-price]');
                const comparePrice = productPriceElement.querySelector('[data-price-compare]');

                stickyProductPrices.forEach((priceElement) => {
                    const stickyPrice = priceElement.querySelector('[data-price]');
                    const stickyComparePrice = priceElement.querySelector('[data-price-compare]');

                    if (stickyPrice && price) stickyPrice.innerHTML = price.innerHTML;
                    if (stickyComparePrice && comparePrice) stickyComparePrice.innerHTML = comparePrice.innerHTML;

                    const badgeText = priceElement.querySelector('.font-600.desktop_show');
                    const buttonPercent = this.btn?.querySelector('.top > span:first-child');

                    if (stickyDiscount !== null) {
                        if (badgeText) {
                            badgeText.textContent = `(${stickyDiscount}% ${this.textOff || 'OFF'})`;
                        }
                        if (buttonPercent) {
                            buttonPercent.textContent = String(stickyDiscount);
                        }
                    } else {
                        if (badgeText) badgeText.textContent = '';
                        if (buttonPercent) buttonPercent.textContent = '';
                    }
                });
            } else {
                stickyProductPrices.forEach((priceElement) => {
                    priceElement.innerHTML = productPriceElement.innerHTML;

                    if (priceElement.classList.contains('mobile_show')) return;

                    const comparePrice = productPriceElement.querySelector('[data-price-compare]');
                    const price = productPriceElement.querySelector('[data-price]');
                    const compareValue = Number(comparePrice?.dataset.value);
                    const priceValue = Number(price?.dataset.value);
                    if (compareValue > priceValue && priceValue >= 0) {
                        priceElement.insertAdjacentHTML(
                            'beforeend',
                            `<span class="font-600" style="order: 2;">(${Math.round((compareValue - priceValue) * 100 / compareValue)}% ${this.textOff})</span>`
                        );
                    }
                });
            }
        } else {
            stickyProductPrices.forEach((priceElement) => {
                priceElement.innerHTML = '';
            });
        }
    }

    getStickyDiscountPercentage(productPriceElement, selectedDiscount = null) {
        const price = productPriceElement.querySelector('[data-price]');
        const comparePrice = productPriceElement.querySelector('[data-price-compare]');
        const priceDatasetDiscount = price?.dataset?.discount || productPriceElement.dataset?.disount || null;
        const discountValue = selectedDiscount || priceDatasetDiscount || null;

        if (!discountValue) {
            const compareValue = Number(comparePrice?.dataset.value);
            const priceValue = Number(price?.dataset.value);
            if (compareValue > priceValue && priceValue >= 0) {
                return Math.round(((compareValue - priceValue) * 100) / compareValue);
            }
            return null;
        }

        if (discountValue.includes('-')) {
            const cents = Number.parseFloat(discountValue.split('-').pop()) || 0;
            const baseValue = Number(comparePrice?.dataset.value || price?.dataset.value || 0);
            
            if (!baseValue) return null;
            return Math.max(0, Math.ceil((cents / baseValue) * 100));
        }

        const percent = Number.parseFloat(discountValue);
        if (Number.isFinite(percent)) return Math.max(0, Math.floor(percent * 100));

        return null;
    }

    bindOptionGroupEvents() {
        this.optionGroups.forEach(group => {
            const selectItem = group.querySelector('.psb-option-group-select_item');
            selectItem.addEventListener('click', (e) => {
                e.stopPropagation(); 
                group.classList.toggle('active');
                
                this.optionGroups.forEach(otherGroup => {
                    if (otherGroup !== group && otherGroup.classList.contains('active')) {
                        otherGroup.classList.remove('active');
                    }
                });
            });

            const options = group.querySelectorAll('.psb-option-group-item');
            options.forEach(option => {
                option.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.handleOptionSelection(group, option);
                });
            });
        });

        document.addEventListener('click', () => {
            this.optionGroups.forEach(group => {
                group.classList.remove('active');
            });
        });
    }

    handleOptionSelection(group, selectedOption) {
        const options = group.querySelectorAll('.psb-option-group-item');
        options.forEach(option => {
            option.classList.remove('checked');
        });
        selectedOption.classList.add('checked');

        // Update the selected value displayed
        const selectItem = group.querySelector('.psb-option-group-select_item>span.text');
        selectItem.textContent = selectedOption.textContent;

        group.classList.remove('active');

        this.syncWithSelectElement(
            selectedOption.dataset.name, 
            selectedOption.dataset.optionValue
        );
    }

    syncWithSelectElement(optionName, optionValue) {
        if (!this.select) return;

        this.productOptionBox.querySelectorAll('product-option-picker').forEach(option => {
            if(option.querySelector('.product-option-picker__list').dataset.optionName == optionName){
                option.querySelector(`.product-option-picker__list-item[data-option-value="${optionValue}"]`).click();
            }
        });
    }

    onVariantChange = () =>{
        const variantId = this.select.options[this.select.selectedIndex].value;
        const productSelector = '.product';

        if(variantId == 'none'){
            this.classList.add('has-no-variant');
        }
        else{
            this.classList.remove('has-no-variant');
        }

        const productAddtocartElement = this.select.closest(productSelector).querySelector('.product-add-to-cart');
        const productThumbnailElements = this.select.closest(productSelector).querySelectorAll('.product-gallery-thumbnails-thumbnail');
        const productOptions = this.select.closest(productSelector).querySelectorAll('.product-option-picker__list');

        const stickyProductPrices = this.select.closest(productSelector).querySelectorAll('.product-sticky-bar-product-price')
        const stickyProductChosenVariant = this.select.closest(productSelector).querySelector('.product-sticky-bar-chosen-variant')
        const stickyProductAddtocart = this.select.closest(productSelector).querySelector('.product-sticky-bar-add-to-cart')
        const stickyProductThumbnail = this.select.closest(productSelector).querySelector('.product-sticky-bar-product-thumbnail')

        if(variantId == 'none'){
            stickyProductPrices.forEach(priceElement => {
                priceElement.innerHTML = '';
            });
        }

        stickyProductChosenVariant.innerHTML = this.select.options[this.select.selectedIndex].innerHTML;

        productOptions.forEach(optionGroup => {
            // Get the name of the product option group and the current selected value
            const optionName = optionGroup.dataset.optionName;
            const chosenOption = optionGroup.querySelector('.product-option-picker__list-item.chosen');
            if (!chosenOption) return;
            
            const chosenValue = chosenOption.dataset.optionValue;

            // Find the corresponding sticky bar option group and update it.
            this.optionGroups.forEach(stickyGroup => {
                //Match the corresponding option group by data attribute
                if (stickyGroup.querySelector('.psb-option-group-item')?.dataset.name === optionName) {
                    // Update the selected value displayed
                    const stickySelectItem = stickyGroup.querySelector('.psb-option-group-select_item>span.text');
                    if (stickySelectItem) {
                        stickySelectItem.textContent = chosenValue;
                    }
                    // Update the selected state in the option group
                    const stickyOptions = stickyGroup.querySelectorAll('.psb-option-group-item');
                    stickyOptions.forEach(option => {
                        if (option.dataset.optionValue === chosenValue) {
                            option.classList.add('checked');
                        } else {
                            option.classList.remove('checked');
                        }
                    });
                }
            });
        });
        
        // Change thumbnail
        for (const thumbnail of productThumbnailElements){
            const imageVariantIds = thumbnail.dataset.variantIds
            if( imageVariantIds.includes(variantId) ){
                stickyProductThumbnail.innerHTML = thumbnail.innerHTML;
            }
        }

        stickyProductAddtocart.querySelector('span[data-text]').dataset.text = productAddtocartElement.querySelector('span[data-text]').dataset.text;
        stickyProductAddtocart.querySelector('span[data-text]').innerHTML = productAddtocartElement.querySelector('span[data-text]').innerHTML;
        stickyProductAddtocart.disabled = productAddtocartElement.disabled;

    }
}
customElements.define('sticky-bar', StickyBar)

if (!customElements.get('product-options')) {
    class ProductOptionPicker extends HTMLElement{
        constructor(){
            super();
            this.form = this.parentElement;
            this.select = this.form.querySelector('select');
            this.productOptions = this.querySelectorAll('ul');
            this.productOptionsList = this.querySelectorAll('li');
            this.productDropdowns = this.querySelectorAll('.product-option-picker__dropdown');
            this.product = {};
            this.chosenOptionsObject = {};
            this.selectOptionsListObject = [];

            this.prepareOptions(this.select);
            this.getProduct().then(res=> this.product = res);

            // Toggle Option dropdown
            for(let dropdown of this.productDropdowns){
                dropdown.addEventListener('click', (event) => {
                    this.dropdown(dropdown, event);
                })

                document.addEventListener('click', (event) => {
                    if (event.target !== dropdown && !dropdown.contains(event.target)) {
                        this.closeDropdown(dropdown)
                    }
                })
            }

            for ( let list of this.productOptions ){
                this.chosenOptionsObject[list.dataset.optionName] = list.dataset.optionValue
            }

            // If variant options present
            this.prepareSelectionListObject();
            var checkVariantsStocksStatus = this.selectOptionsListObject.every(option => ((option.available && option.stock <= 0) || option.policy != 'deny'))
            
            if( !checkVariantsStocksStatus ){
                this.checkOptionsAvailability();
            }

            document.body.addEventListener('click', (event) => {
                var option = event.target.closest('.product-option-picker__list-item')
                if( option && !option.classList.contains('chosen') ){
                    this.selectionPicker(option);

                    // Option variant picker
                    this.onClick(option);
                    
                    // Option availability on click
                    var checkVariantsStocksStatus = this.selectOptionsListObject.every(option => ((option.available && option.stock <= 0) || option.policy != 'deny'))
                    if( !checkVariantsStocksStatus ){
                        this.checkOptionsAvailabilityOnClick(option);
                    }
                }
            })
            
            for (let productOption of this.productOptionsList){
                productOption.addEventListener('focus', () => {
                    productOption.addEventListener('keyup', (event) => {
                        if(event.key === 'Enter'){
                            productOption.click();
                        }
                    })
                });
            }
        }

        getProduct = async () => {
            var handle = this.dataset.productHandle;

            return await fetch(`${window.Shopify.routes.root}products/${handle}.js`)
            .then((response) => {
                if (!response.ok) {
                    var error = new Error(response.status);
                    throw error;
                }

                return response.json();
            })
            .catch((error) => {
                throw error;
            });
        }

        prepareOptions(select){
            var availableOptions = [];
            var selectOptionsList = [];

            for ( let list of this.productOptions ){
                availableOptions.push(list.dataset.optionName)
            }

            for (let selectOption of select) {
                var matchFound = false;
                for (let productOption of this.productOptionsList){
                
                if( selectOption.innerText.trim() == productOption.dataset.optionValue ){
                    matchFound = true;
                }
                }

                if( matchFound ){
                selectOptionsList.push([selectOption.text.trim()])
                }
                else{
                var selectOptionArray = selectOption.text.split(' / ');

                this.productOptions.forEach(productOptions => {
                    productOptions.querySelectorAll('li').forEach(productOption => {
                    var productOptionValue = productOption.dataset.optionValue
                    if(productOptionValue.includes(' / ')){
                        if(selectOption.text.includes(productOption.dataset.optionValue)){
                        var newValue = productOption.dataset.optionValue.split(' / ');
                        var newSelectOption = selectOption.text.replace(productOption.dataset.optionValue, newValue.join(' $/ '));
                        selectOptionArray = newSelectOption.split(' / ');
                        }
                    }
                    })
                })

                selectOptionsList.push(selectOptionArray.map((string) => { 
                    var newString = string.replace(/\$\//gi, '/')
                    newString = newString.trim()
                    return newString
                }))
                }
            }

            for (let optionValueArray of selectOptionsList){
                var availableOptionsObject = {}

                availableOptions.map(function(optionName, i){
                    availableOptionsObject[optionName] = optionValueArray[i];
                })

                this.selectOptionsListObject.push( availableOptionsObject )            
            }
        }

        onClick(option){
            this.productOptions = option.closest('product-options').querySelectorAll('ul') 
            this.chosenOptionsObject = {};
            this.selectOptionsListObject = [];
            this.productOptions.forEach(list => {
                for(let list_item of list.children){
                    if( list_item.classList.contains('chosen') ){
                        var chosenOptionValue = list_item.dataset.optionValue
                        var chosenOptionName = list.dataset.optionName
                        this.chosenOptionsObject[chosenOptionName] = chosenOptionValue;
                    }
                }
            })

            this.select = option.closest('.product').querySelector('.product-options-form select');

            this.prepareOptions(this.select);
            this.prepareSelectionListObject();

            this.selectOptionsListObject.forEach((option) => {
                const newOption = Object.assign({}, option)
                delete newOption.available
                delete newOption.stock
                delete newOption.management
                delete newOption.index
                delete newOption.policy
                
                if( JSON.stringify(newOption) === JSON.stringify(this.chosenOptionsObject) ){
                    
                    if( typeof option.index != 'undefined' ){
                        this.select.selectedIndex = option.index;
                        this.select.dispatchEvent(new Event('change'));

                        var variantId = this.select[option.index].getAttribute('value');
                        var newUrl = new URL(window.location.href);
                        
                        if( newUrl.searchParams.get('variant') !== null ){
                            newUrl.searchParams.set('variant', variantId)
                        }
                        else{
                            newUrl.searchParams.append('variant', variantId);
                        }
                                            
                        if(!Shopify.designMode){
                            history.pushState({}, null, newUrl);
                        }
                    }
                    else{
                        this.select.selectedIndex = 0;
                        this.select.dispatchEvent(new Event('change'));
                    }
                }
            })
        }

        selectionPicker(option){
            var currentDropdown = option.parentElement //event.target.parentElement;
            var currentDropdownListItems = currentDropdown.querySelectorAll('li');
            var chosenElements = currentDropdown.parentElement.parentElement.querySelectorAll('.product-option-picker-chosen > span, .product-option-picker-label > .product-option-picker-label--chosen');
            var chosenSwatchElement = currentDropdown.parentElement.parentElement.querySelector('.product-option-picker-chosen > .product-option-swatch')
            var chosenOptionSwatch = option.querySelector('.product-option-swatch');
            
            for(let item of currentDropdownListItems){
                item.classList.remove('chosen');
            }
            option.classList.add('chosen');

            for(let chosen of chosenElements){
                chosen.innerText = option.textContent.trim()//event.target.innerText
            }
            
            if( chosenOptionSwatch ){
                //console.log("contains children", chosenOptionSwatch.children);
                var optionProductSwatchStyles = getComputedStyle(chosenOptionSwatch)
                var optionSwatchColor = optionProductSwatchStyles.getPropertyValue('--product-swatch-value');
                if( optionSwatchColor.length == 0 ){
                    optionSwatchColor = option.dataset.optionValue
                }
                if( chosenSwatchElement ){
                    chosenSwatchElement.innerHTML = chosenOptionSwatch.innerHTML;
                    chosenSwatchElement.style.setProperty('--product-swatch-value', optionSwatchColor);
                }
            }
        }

        
        prepareSelectionListObject(){

            var newSelectOptions = this.selectOptionsListObject.map(obj => Object.assign({}, obj))

            this.select.querySelectorAll('option').forEach((option, index) => {
                newSelectOptions[index].stock = parseInt(option.dataset.inventoryQuantity);
                newSelectOptions[index].management = option.dataset.inventoryManagement;
                newSelectOptions[index].policy = option.dataset.inventoryPolicy;
            })
            
            var customSelectOptions = [];
            Object.entries(this.chosenOptionsObject).forEach(chosenOption => {
                var eachOptions = [];
                this.productOptions.forEach((productOption, index) => {
                    if( chosenOption[0] == productOption.dataset.optionName ){
                        for(let option of productOption.children){
                            eachOptions.push({ [productOption.dataset.optionName]: option.dataset.optionValue});
                        }
                    }
                })
                customSelectOptions.push(eachOptions);

            })
            
            const generateCombinations = (data) => {
                const result = [];
                const resultTrimmed = []
                function helper(temp, index) {
                if (temp.length === data.length) {
                    result.push(temp);
                    return;
                }
                for (let i = 0; i < data[index].length; i++) {
                    helper([...temp, data[index][i]], index + 1);
                }
                }
                helper([], 0);

                result.forEach(data => {
                    const obj = data.reduce((result, item) => {
                        return { ...result, ...item };
                    }, {});
                    resultTrimmed.push(obj)
                })
                return resultTrimmed;
            }

            let newcustomSelectOptions = generateCombinations([...customSelectOptions]);
            let availableKeys = Object.keys(newcustomSelectOptions[0]);
            
            const finalSelectOptions = newcustomSelectOptions.map((selectOption) => {
                let newSelectOption = Object.assign({}, selectOption);
                newSelectOptions.forEach((option, index) => {
                    if( availableKeys.every(key => {
                        return option[key] == selectOption[key]
                    }) ){
                        selectOption.available = true
                        newSelectOption = {...selectOption, ...option, index}
                    }
                    else{
                        if( !selectOption.hasOwnProperty('available') ){
                            selectOption.available = false
                            newSelectOption = selectOption
                        }
                    }
                })

                return newSelectOption
            })
            this.selectOptionsListObject = finalSelectOptions;
        }

        checkOptionsAvailability(){
            var newSelectOptions = this.selectOptionsListObject.map(obj => Object.assign({}, obj))
            var productOptionsArray = {};

            this.productOptions.forEach(productOption => {
                productOptionsArray[productOption.dataset.optionName] = Array.from(productOption.children).map(option => option.dataset.optionValue)
            })

            if( Object.keys(productOptionsArray).length == 1 ){
                newSelectOptions.forEach(option => {
                    if((option.stock <= 0 && option.management != '' && option.policy != 'continue') || !option.available){
                        this.productOptions.forEach(productOption => {
                            for(let optionValue of productOption.children){
                                if(option[productOption.dataset.optionName] == optionValue.dataset.optionValue){
                                    optionValue.classList.add('disabled');
                                }
                            }
                        })
                    }
                })
            }

            this.productOptions.forEach(productOption => {
                let filterdSelectOptions = newSelectOptions.filter(option => {
                    return option[productOption.dataset.optionName] != this.chosenOptionsObject[productOption.dataset.optionName] 
                })

                for(let optionValue of productOption.children){
                    if( optionValue.dataset.optionValue !== this.chosenOptionsObject[productOption.dataset.optionName] ){
                        if(filterdSelectOptions.every(option => {
                            return (option.stock <= 0 && option.management != '' && option.policy != 'continue') || !option.available
                        })){
                            
                            optionValue.classList.add('disabled');
                        }
                        else{
                            var chosenOptionsObject = Object.assign({}, this.chosenOptionsObject);
                            delete chosenOptionsObject[productOption.dataset.optionName]
                            
                            filterdSelectOptions.forEach((option) => {                            
                                var chosenSelectOptions = Object.entries(chosenOptionsObject).map(chosenOption => {
                                    return option[chosenOption[0]] == chosenOption[1]
                                })

                                if(chosenSelectOptions.every(option => option)){
                                    if((option.stock <= 0 && option.management != '' && option.policy != 'continue') || !option.available){
                                        if( option[productOption.dataset.optionName] == optionValue.dataset.optionValue ){
                                            optionValue.classList.add('disabled');
                                        }
                                    }
                                }
                            })
                            
                            filterdSelectOptions.forEach((option) => {
                                if( option.available && (option.stock <= 0 || option.management == '' || option.policy == 'continue') ){
                                    if( option[productOption.dataset.optionName] == optionValue.dataset.optionValue ){
                                        if( optionValue.classList.contains('disabled') ){
                                            optionValue.classList.add('unavailable');
                                            optionValue.classList.remove('disabled');
                                        }
                                    }
                                }
                            })
                        }
                    }
                }
            });
        }

        checkOptionsAvailabilityOnClick(option){
            var productOptionsList = option.closest('product-options').querySelectorAll('li')//this.productOptionsList;

            for(let productOption of productOptionsList){
                productOption.classList.remove('disabled');
                productOption.classList.remove('unavailable');
            }
            
            var newSelectOptions = this.selectOptionsListObject.map(obj => Object.assign({}, obj))
            var productOptionsArray = {};
            
            this.productOptions.forEach(productOption => {
                productOptionsArray[productOption.dataset.optionName] = Array.from(productOption.children).map(option => option.dataset.optionValue)
            })

            this.productOptions.forEach(productOption => {
                let filterdSelectOptions = newSelectOptions;
                
                for(let optionValue of productOption.children){
                    if(filterdSelectOptions.every(option => { return (option.stock <= 0 && option.management != '' && option.policy != 'continue') || !option.available } )){
                        optionValue.classList.add('disabled');
                    }
                    else{
                        var chosenOptionsObject = Object.assign({}, this.chosenOptionsObject);
                        delete chosenOptionsObject[productOption.dataset.optionName]
                        
                        filterdSelectOptions.forEach((option) => {
                            var chosenSelectOptions = Object.entries(chosenOptionsObject).map(chosenOption => {
                                return option[chosenOption[0]] == chosenOption[1]
                            })

                            if(chosenSelectOptions.every(option => option)){                                
                                if((option.stock <= 0 && option.management != '' && option.policy != 'continue') || !option.available){
                                    if( option[productOption.dataset.optionName] == optionValue.dataset.optionValue ){
                                        optionValue.classList.add('disabled');
                                    }
                                }
                            }
                        })

                        filterdSelectOptions.forEach((option) => {
                            if( option.available && ( option.stock <= 0 || option.management == '' || option.policy == 'continue') ){
                                if( option[productOption.dataset.optionName] == optionValue.dataset.optionValue ){
                                    if( optionValue.classList.contains('disabled') ){
                                        optionValue.classList.add('unavailable');
                                        optionValue.classList.remove('disabled');
                                    }
                                }
                            }
                        })
                    }
                }
            });
        }   

        dropdown(dropdown, event){
                if( dropdown.classList.contains('active') ){
                    this.closeDropdown(dropdown)
                    dropdown.blur();
                }
                else{
                    if( !dropdown.matches(':focus-within:not(:focus)') ){
                        this.openDropdown(dropdown)
                    }
                }
        }

        openDropdown(dropdown){
            dropdown.classList.add('active')
        }
        closeDropdown(dropdown){
            dropdown.classList.remove('active')
        }
    }
    customElements.define( 'product-options', ProductOptionPicker )
}

if (!customElements.get('custom-countdown')) {
    class CustomCountdown extends HTMLElement {
        constructor() {
            super();
            this.attachShadow({
                mode: 'open'
            });
            this.timer = null;
            this.targetDate = null
        }
        static get observedAttributes() {
            return ['start-date', 'cycle', 'show-labels', 'data-day', 'data-hour', 'data-mins', 'data-secs']
        }
        connectedCallback() {
            this.render();
            this.initTarget()
        }
        attributeChangedCallback(name) {
            if (this.isConnected){
                if (name === 'show-labels' || name.startsWith('data-')) {
                    this.render();
                }
                this.initTarget()
            }
        }
        initTarget() {
            clearInterval(this.timer);
            let startDateStr = this.getAttribute('start-date');
            const cycleDays = parseFloat(this.getAttribute('cycle')) || 1;
            
            if (!startDateStr) return;

            const formattedDateStr = startDateStr.replace(/-/g, '/').replace(/T/g, ' ');
            this.targetDate = new Date(formattedDateStr);

            if (isNaN(this.targetDate.getTime())) {
                console.error('Invalid Date on this device:', startDateStr);
                return;
            }

            const now = new Date();
            if (this.targetDate.getTime() <= now.getTime()) {
                const diff = now.getTime() - this.targetDate.getTime();
                const msPerCycle = cycleDays * 24 * 60 * 60 * 1000;
                const cyclesPassed = Math.floor(diff / msPerCycle) + 1;
                this.targetDate.setTime(this.targetDate.getTime() + cyclesPassed * msPerCycle);
            }
            this.start();
        }
        start() {
            const update = () => {
                const now = new Date();
                let timeLeft = this.targetDate.getTime() - now.getTime();
                if (timeLeft <= 0) {
                    const cycleDays = parseFloat(this.getAttribute('cycle')) || 1;
                    this.targetDate.setTime(this.targetDate.getTime() + (cycleDays * 24 * 60 * 60 * 1000));
                    timeLeft = this.targetDate.getTime() - now.getTime()
                }
                this.updateDisplay(timeLeft)
            };
            update();
            this.timer = setInterval(update, 1000)
        }
        updateDisplay(ms) {
            const dayBlock = this.shadowRoot.getElementById('day-block');
            const daySep = this.shadowRoot.getElementById('day-sep');
            if (!dayBlock || !daySep) return;
            const d = Math.floor(ms / 86400000);
            const h = Math.floor((ms % 86400000) / 3600000);
            const m = Math.floor((ms % 3600000) / 60000);
            const s = Math.floor((ms % 60000) / 1000);
            if (d <= 0) {
                dayBlock.style.display = 'none';
                daySep.style.display = 'none'
            } else {
                dayBlock.style.display = 'flex';
                daySep.style.display = 'block';
                this.shadowRoot.getElementById('d').textContent = d.toString().padStart(2, '0')
            }
            this.shadowRoot.getElementById('h').textContent = h.toString().padStart(2, '0');
            this.shadowRoot.getElementById('m').textContent = m.toString().padStart(2, '0');
            this.shadowRoot.getElementById('s').textContent = s.toString().padStart(2, '0')
        }
        render() {
            const showLabels = this.hasAttribute('show-labels');
            
            const dayLabel = this.getAttribute('data-day') || 'Days';
            const hourLabel = this.getAttribute('data-hour') || 'Hrs';
            const minsLabel = this.getAttribute('data-mins') || 'Mins';
            const secsLabel = this.getAttribute('data-secs') || 'Secs';

            this.shadowRoot.innerHTML = `
                <style>
                :host { display: inline-block; font-family: inherit; }
                .container { display: flex; align-items: center; gap: 2px; }
                .time-block {${showLabels ? 'display: flex; flex-direction: column; align-items: center; gap: 2px;':''}}
                .number { 
                    background: #261622; color: #fff; font-size: 12px; font-weight: 500; 
                    border-radius: 4px; min-width: 24px; min-height: 24px; 
                    display: flex; justify-content: center; align-items: center; 
                }
                .label { 
                    font-size: 8px; color: #261622; font-weight: 500;
                    display: ${showLabels ? 'block' : 'none'};
                }
                .separator { 
                    padding-bottom: ${showLabels ? '12px' : '0px'};
                }
                #day-block { display: flex; }
            </style>
            <div class="container" part="container">
                <div id="day-block" class="time-block">
                    <div class="number" part="number-box" id="d">00</div>
                    <span class="label" part="label">${dayLabel}</span>
                </div>
                <div class="separator" part="separator" id="day-sep">:</div>
                <div class="time-block">
                    <div class="number" part="number-box" id="h">00</div>
                    <span class="label" part="label">${hourLabel}</span>
                </div>
                <div class="separator" part="separator">:</div>
                <div class="time-block">
                    <div class="number" part="number-box" id="m">00</div>
                    <span class="label" part="label">${minsLabel}</span>
                </div>
                <div class="separator" part="separator">:</div>
                <div class="time-block">
                    <div class="number" part="number-box" id="s">00</div>
                    <span class="label" part="label">${secsLabel}</span>
                </div>
            </div>
            `
        }
        disconnectedCallback() {
            clearInterval(this.timer)
        }
    }
    customElements.define('custom-countdown', CustomCountdown)
}